import { detectLoggedOutResponse } from '@/lib/auth-response-handler'
import Axios, { type AxiosInstance, type AxiosRequestConfig, type CreateAxiosDefaults } from 'axios'

type BodylessMethod = <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>
type BodyMethod = <T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>
) => Promise<T>

/**
 * The default client unwraps `response.data` in its response interceptor, so every
 * request resolves to the response body. Pass the body type explicitly:
 * `axios.get<ApiTokenState>('/api-token', config)`.
 */
export interface ApiClient extends Omit<
  AxiosInstance,
  | 'request'
  | 'get'
  | 'delete'
  | 'head'
  | 'options'
  | 'post'
  | 'put'
  | 'patch'
  | 'postForm'
  | 'putForm'
  | 'patchForm'
> {
  // Omit drops AxiosInstance's call signatures; `axios(url, config)` defaults to a GET.
  <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  request<T = unknown, D = unknown>(config: AxiosRequestConfig<D>): Promise<T>
  get: BodylessMethod
  delete: BodylessMethod
  head: BodylessMethod
  options: BodylessMethod
  post: BodyMethod
  put: BodyMethod
  patch: BodyMethod
  postForm: BodyMethod
  putForm: BodyMethod
  patchForm: BodyMethod
}

const axiosConfig: CreateAxiosDefaults = {
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  withXSRFToken: true,
}

export const rawAxios = Axios.create(axiosConfig)

const axios = Axios.create(axiosConfig)

// Nothing should ever return a 401 except the API telling us our session has expired
// So, 401 response errors should always redirect to the login page
// Ignore 401 errors for the logged in user as we run those on the login page itself
axios.interceptors.response.use(
  // oxlint-disable-next-line typescript/no-unsafe-return -- the client resolves to the response body; ApiClient types it per call
  (response) => response?.data,
  (error) => detectLoggedOutResponse(error)
)

rawAxios.interceptors.response.use(
  (response) => response,
  (error) => detectLoggedOutResponse(error)
)

// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- AxiosInstance's types can't express the body-unwrapping interceptor above
export default axios as unknown as ApiClient
